import { palette } from '../design-tokens/palette';
import type { SemanticTokens } from '../design-tokens/semantic';

/**
 * Dark Theme
 *
 * Maps semantic tokens to palette values for dark mode.
 * Currently minimal - can be expanded when dark mode is needed.
 *
 * Dark mode principles:
 * - Lower contrast than light mode (reduces eye strain)
 * - Elevated surfaces are lighter, not darker
 * - Avoid pure black (#000000)
 */

export const darkTheme: SemanticTokens = {
  background: {
    app: palette.neutral[950], // Very dark gray, not pure black
    surface: palette.neutral[900], // Slightly lighter for cards
    surfaceSubtle: palette.neutral[800], // Elevated surfaces
    subtle: palette.neutral[800], // Groups, pill toggles
    muted: palette.neutral[800], // Disabled/muted backgrounds
    input: palette.neutral[800], // Input/select backgrounds
    hover: palette.neutral[800],
    selected: palette.primary[900],
    overlay: 'rgba(0, 0, 0, 0.7)',
  },

  text: {
    primary: palette.neutral[50], // Off-white
    secondary: palette.neutral[300],
    muted: palette.neutral[400], // Bumped from 500 → 400 for WCAG AA contrast
    tertiary: palette.neutral[500], // Very de-emphasized (locked items, disabled)
    inverse: palette.neutral[900],
    link: palette.primary[400],
    linkHover: palette.primary[300],
    brand: palette.primary[400], // Brand-colored text (bright in dark mode)
    danger: palette.danger[400], // Danger/error text
    warning: palette.warning[400], // Warning text
  },

  border: {
    default: palette.neutral[700],
    subtle: palette.neutral[700], // Bumped from 800 → 700 so borders are visible on 900 surfaces
    strong: palette.neutral[600],
    focus: palette.primary[500],
    warning: palette.warning[700], // Warning state borders
  },

  accent: {
    primary: palette.primary[500],
    primaryHover: palette.primary[400],
    primaryActive: palette.primary[300],
    primarySubtle: palette.primary[900],

    secondary: palette.neutral[400],
    secondaryHover: palette.neutral[300],
    secondaryActive: palette.neutral[200],

    success: palette.success[500],
    successHover: palette.success[400],
    successSubtle: palette.success[900],

    warning: palette.warning[500],
    warningHover: palette.warning[400],
    warningSubtle: palette.warning[900],

    danger: palette.danger[500],
    dangerHover: palette.danger[400],
    dangerSubtle: palette.danger[900],
  },

  state: {
    disabled: palette.neutral[800],
    disabledText: palette.neutral[600],
    focus: palette.primary[500],
    selected: palette.primary[900],
    selectedText: palette.primary[300],
  },

  status: {
    successBg: palette.success[900],
    successText: palette.success[300],
    successBorder: palette.success[700],

    warningBg: palette.warning[900],
    warningText: palette.warning[300],
    warningBorder: palette.warning[700],

    dangerBg: palette.danger[900],
    dangerText: palette.danger[300],
    dangerBorder: palette.danger[700],

    infoBg: palette.primary[900],
    infoText: palette.primary[300],
  },

  badge: {
    blueBg: 'rgba(30, 58, 138, 0.3)', // primary.900 at 30%
    blueText: palette.primary[300],
    greenBg: 'rgba(20, 83, 45, 0.3)', // success.900 at 30%
    greenText: palette.success[300],
    redBg: 'rgba(127, 29, 29, 0.3)', // danger.900 at 30%
    redText: palette.danger[300],
    amberBg: 'rgba(120, 53, 15, 0.3)', // amber.900 at 30%
    amberText: palette.amber[300],
    purpleBg: 'rgba(88, 28, 135, 0.3)', // purple.900 at 30%
    purpleText: palette.purple[300],
    yellowBg: 'rgba(120, 53, 15, 0.3)', // warning.900 at 30%
    yellowText: palette.warning[300],
    skyBg: 'rgba(12, 74, 110, 0.3)', // sky.900 at 30%
    skyText: palette.sky[300],
    indigoBg: 'rgba(49, 46, 129, 0.3)', // indigo.900 at 30%
    indigoText: palette.indigo[300],
    emeraldBg: 'rgba(6, 78, 59, 0.3)', // emerald.900 at 30%
    emeraldText: palette.emerald[300],
    grayBg: 'rgba(64, 64, 64, 0.3)', // neutral.700 at 30%
    grayText: palette.neutral[300],
  },
};
